// react
import { Suspense, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useParams } from '@tanstack/react-router';

// material ui
import { Box } from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';

// api
import type { PortfolioAnalysisOutput } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';

// feature-components
import PortfolioAnalysisDashboardDatagrid from '@/features/energyxplorer/portfolio/components/datagrid/overview-datagrid/PortfolioAnalysisDashboardDatagrid';
import usePortfolioAnalysisRows from '@/features/energyxplorer/portfolio/hooks/usePortfolioAnalysisRows';
// utils
import {
  calculatePortfolioReMoniFactors,
  type PortfolioReMoniFactor,
} from '@/utils/remoniScoreCalculator';

// relative
import PortfolioHighlightDetailsDrawer from './PortfolioHighlightDetailsDrawer';
import RemoniFactorCell from './RemoniFactorCell';

interface PortfolioOverviewDatagridProps {
  powerData: PortfolioAnalysisOutput | undefined;
  heatData: PortfolioAnalysisOutput | undefined;
  waterData: PortfolioAnalysisOutput | undefined;
}

type AnalysisType = 'power' | 'heat' | 'water';

function getAnalysisType(metric: PortfolioReMoniFactor): AnalysisType {
  if (metric.highestResource === 'electricity') return 'power';
  if (metric.highestResource === 'heat') return 'heat';
  if (metric.highestResource === 'water') return 'water';
  if (metric.electricity) return 'power';
  if (metric.heat) return 'heat';
  return 'water';
}

const PortfolioOverviewDatagrid = ({
  powerData,
  heatData,
  waterData,
}: PortfolioOverviewDatagridProps) => {
  const { t } = useTranslation('energy-analysis');
  const { accountId } = useParams({ strict: false });
  const navigate = useNavigate() as (args: Record<string, unknown>) => void;
  const [selectedDebugMetric, setSelectedDebugMetric] =
    useState<PortfolioReMoniFactor | null>(null);
  // Datagrid reports the row type right before the row itself, in the same click
  const meterTypeRef = useRef<'matrikel' | 'meter'>('matrikel');
  const { enrichedData, matrikel_list, isFetching } =
    usePortfolioAnalysisRows();

  const metricsByMatrikel = new Map(
    calculatePortfolioReMoniFactors(
      powerData?.portfolio_analysis_list,
      heatData?.portfolio_analysis_list,
      waterData?.portfolio_analysis_list,
    ).map((metric) => [metric.matrikelKey, metric]),
  );

  const openAnalysis = (
    analysisType: AnalysisType,
    search: Record<string, unknown>,
  ) => {
    navigate({
      to: `/${accountId}/energyxplorer/portfolio/${analysisType}/analysis/overview`,
      search,
    });
  };

  const handleOpenAnalysisFromMetric = (metric: PortfolioReMoniFactor) => {
    const analysisType = getAnalysisType(metric);
    openAnalysis(analysisType, {
      meterId: metric.matrikelKey,
      meterRowType: 'matrikel',
      drawerAnalysisType: analysisType,
    });
  };

  const handleSelectMeter = (row: Record<string, unknown>) => {
    const matrikelKey = row.matrikel_key as string;
    const meterType = meterTypeRef.current;
    const metric = metricsByMatrikel.get(matrikelKey);
    const analysisType: AnalysisType =
      meterType === 'meter'
        ? ((row.type as AnalysisType) ?? 'power')
        : metric
          ? getAnalysisType(metric)
          : 'power';

    openAnalysis(analysisType, {
      meterId: matrikelKey,
      meterSubId: row.UnitId
        ? String(row.UnitId)
        : row.MeteringPointId
          ? String(row.MeteringPointId)
          : undefined,
      meterRowType: meterType,
      drawerAnalysisType: analysisType,
    });
  };

  const remoniColumn: GridColDef = {
    field: 'remoniScore',
    headerName: t('portfolio.highlights.remoni_score'),
    type: 'number',
    width: 140,
    align: 'right',
    headerAlign: 'right',
    groupable: false,
    aggregable: false,
    valueGetter: (_value, row) =>
      row.path?.length === 1
        ? (metricsByMatrikel.get(row.matrikel_key)?.remoniFactor ?? null)
        : null,
    renderCell: (params: GridRenderCellParams) => {
      const metric =
        params.row.path?.length === 1
          ? metricsByMatrikel.get(params.row.matrikel_key)
          : undefined;
      return metric ? (
        <RemoniFactorCell
          metric={metric}
          onOpenDebugDetails={setSelectedDebugMetric}
        />
      ) : null;
    },
  };

  if (!enrichedData && !isFetching) return null;

  return (
    <Box>
      <Box display="flex" flexDirection="column">
        <PortfolioAnalysisDashboardDatagrid
          data={enrichedData ?? { portfolio_analysis_list: [] }}
          matrikelList={matrikel_list}
          setSelectedMeter={handleSelectMeter as (row: unknown) => void}
          setMeterType={(type) => {
            meterTypeRef.current = type as 'matrikel' | 'meter';
          }}
          isLoading={isFetching}
          extraColumns={[remoniColumn]}
        />
      </Box>
      <Suspense>
        <PortfolioHighlightDetailsDrawer
          metric={selectedDebugMetric}
          open={Boolean(selectedDebugMetric)}
          onClose={() => setSelectedDebugMetric(null)}
          onOpenAnalysis={handleOpenAnalysisFromMetric}
        />
      </Suspense>
    </Box>
  );
};

export default PortfolioOverviewDatagrid;
